import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CampaignDraft } from "src/services/CampaignDraftTypes";
import type { CampaignData } from "../../../../../EngagementInterfaces";
import type { EmailDesign } from "../../AiEmail/AiCampaignTypes";
import { templateToDraftContent, withoutLists } from "../../../../shared/CampaignDraftUtils";
import { useCampaignDraft, type CampaignDraftState } from "../../../../shared/UseCampaignDraft";
import { useDraftWarnings } from "../../../../shared/UseDraftWarnings";
import {
  getCampaignDetailsError,
  getSenderRecipientsError,
} from "../../AiEmail/AiCampaignUtils";
import { useCampaignBasics } from "../../AiEmail/hooks/UseCampaignBasics";
import { EMAIL_TEMPLATE_WIZARD_STEPS, type EmailTemplateStepKey } from "../TemplateEditorConstants";
import {
  clearTemplateWizardSnapshot,
  readTemplateWizardSnapshot,
  writeTemplateWizardSnapshot,
} from "../TemplateWizardSnapshot";
import { buildTemplateDraftData, restoreTemplateDraft } from "../TemplateCampaignDraft";
import { getPreviousCampaignTemplate, useTemplateSelection } from "./UseTemplateSelection";

const EDITOR_STEP_INDEX = EMAIL_TEMPLATE_WIZARD_STEPS.findIndex(step => step.key === "editor");
const CHOOSE_TEMPLATE_STEP_INDEX = EMAIL_TEMPLATE_WIZARD_STEPS.findIndex(
  step => step.key === "chooseTemplate"
);

interface EmailTemplateWizardOptions {
  initialStepIndex?: number;
  persist?: boolean;
  initialDraft?: CampaignDraft | null;
  draftController?: CampaignDraftState;
}

export const useEmailTemplateWizard = (
  campaignData: Partial<CampaignData>,
  {
    initialStepIndex = 0,
    persist = true,
    initialDraft = null,
    draftController,
  }: EmailTemplateWizardOptions = {}
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storageKey = useLocation().key;
  const [saved] = useState(() => (persist ? readTemplateWizardSnapshot(storageKey) : null));
  const ownDraft = useCampaignDraft(initialDraft);
  const draft = draftController ?? ownDraft;
  const { closeDraft } = draft;
  const { missingLists, isNameTaken } = useDraftWarnings(initialDraft);
  const [resumed] = useState(() => {
    if (!initialDraft || saved) {
      return null;
    }
    const restored = restoreTemplateDraft(initialDraft);
    return {
      ...restored,
      sendTo: withoutLists(restored.sendTo, missingLists),
      excludeList: withoutLists(restored.excludeList, missingLists),
      stepIndex: missingLists.length > 0 || isNameTaken ? 0 : restored.stepIndex,
    };
  });
  const [initialTemplate] = useState(
    () =>
      saved?.template ??
      resumed?.template ??
      getPreviousCampaignTemplate(campaignData.emailHtml)
  );
  const [stepIndex, setStepIndex] = useState(() => {
    const restoredIndex = saved?.stepIndex ?? resumed?.stepIndex;
    if (restoredIndex !== undefined) {
      const index = Math.min(Math.max(restoredIndex, 0), EDITOR_STEP_INDEX);
      return !initialTemplate && index >= CHOOSE_TEMPLATE_STEP_INDEX + 1
        ? CHOOSE_TEMPLATE_STEP_INDEX
        : index;
    }
    return initialTemplate ? EDITOR_STEP_INDEX : initialStepIndex;
  });
  const [campaignName, setCampaignName] = useState(
    saved?.campaignName ?? resumed?.campaignName ?? campaignData.campaignName ?? ""
  );
  const [subjectLine, setSubjectLine] = useState(saved?.subjectLine ?? resumed?.subjectLine ?? "");

  const stepKey: EmailTemplateStepKey = EMAIL_TEMPLATE_WIZARD_STEPS[stepIndex].key;
  const furthestIndexRef = useRef(stepIndex);
  furthestIndexRef.current = Math.max(furthestIndexRef.current, stepIndex);
  const basics = useCampaignBasics(
    campaignData,
    saved ?? resumed ?? {},
    stepKey === "senderRecipients"
  );
  const {
    sendTo,
    excludeList,
    replyTo,
    isCheckingName,
    clearNameError,
    verifyCampaignName,
    setSendTo,
    setExcludeList,
    setReplyTo,
  } = basics;

  const showEditor = useCallback(() => setStepIndex(EDITOR_STEP_INDEX), []);
  const selection = useTemplateSelection({ campaignName, initialTemplate, onApplied: showEditor });
  const { template, resetSelection } = selection;

  useEffect(() => {
    if (!persist) {
      return;
    }
    writeTemplateWizardSnapshot(storageKey, {
      stepIndex,
      campaignName,
      subjectLine,
      sendTo,
      excludeList,
      replyTo,
      template,
    });
  }, [campaignName, excludeList, persist, replyTo, sendTo, stepIndex, storageKey, subjectLine, template]);

  useEffect(
    () => () => {
      if (persist) {
        clearTemplateWizardSnapshot(storageKey);
      }
    },
    [persist, storageKey]
  );

  const validationContext = useMemo(() => ({ sendTo, replyTo }), [replyTo, sendTo]);
  const getStepError = useCallback(
    (key: EmailTemplateStepKey) => {
      switch (key) {
        case "campaignDetails":
          return getCampaignDetailsError(campaignName, sendTo);
        case "senderRecipients":
          return getSenderRecipientsError(subjectLine, validationContext);
        default:
          return null;
      }
    },
    [campaignName, sendTo, subjectLine, validationContext]
  );
  const stepError = getStepError(stepKey);

  const needsTemplate = stepKey === "chooseTemplate" || stepKey === "editor";
  const canProceed = !isCheckingName && !stepError && (!needsTemplate || Boolean(template));

  const updateCampaignName = useCallback(
    (value: string) => {
      clearNameError();
      setCampaignName(value);
    },
    [clearNameError]
  );

  const goNext = useCallback(async () => {
    if (stepError) {
      message.error(t(stepError));
      return;
    }
    if (stepKey === "campaignDetails" && !(await verifyCampaignName(campaignName, draft.draftId))) {
      return;
    }
    setStepIndex(current => Math.min(current + 1, EDITOR_STEP_INDEX));
  }, [campaignName, draft.draftId, stepError, stepKey, t, verifyCampaignName]);

  const goToStep = useCallback(
    async (target: number) => {
      if (target <= stepIndex) {
        setStepIndex(target);
        return;
      }
      if (stepKey === "campaignDetails" && !(await verifyCampaignName(campaignName))) {
        return;
      }
      for (let index = stepIndex; index < target; index += 1) {
        const step = EMAIL_TEMPLATE_WIZARD_STEPS[index].key;
        const errorKey = getStepError(step);
        if (errorKey || ((step === "chooseTemplate" || step === "editor") && !template)) {
          if (errorKey) {
            message.error(t(errorKey));
          }
          setStepIndex(index);
          return;
        }
      }
      setStepIndex(target);
    },
    [campaignName, getStepError, stepIndex, stepKey, t, template, verifyCampaignName]
  );

  const goBack = useCallback(() => {
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex(current => current - 1);
  }, [navigate, stepIndex]);

  const buildDraftData = useCallback(
    () =>
      buildTemplateDraftData({ stepIndex, campaignName, subjectLine, sendTo, excludeList, replyTo }),
    [campaignName, excludeList, replyTo, sendTo, stepIndex, subjectLine]
  );

  const { save: saveToDraft, savedSnapshot } = draft;
  const saveDraft = useCallback(
    (design?: EmailDesign | null) =>
      saveToDraft(buildDraftData(), templateToDraftContent(template, design)),
    [buildDraftData, saveToDraft, template]
  );
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(buildDraftData()) !== savedSnapshot,
    [buildDraftData, savedSnapshot]
  );

  const reset = useCallback(
    (nextCampaignData: Partial<CampaignData>) => {
      closeDraft();
      const previousTemplate = getPreviousCampaignTemplate(nextCampaignData.emailHtml);
      resetSelection(previousTemplate);
      setStepIndex(previousTemplate ? EDITOR_STEP_INDEX : initialStepIndex);
      setCampaignName(nextCampaignData.campaignName ?? "");
      setSubjectLine("");
      setSendTo(nextCampaignData.sendTo ?? []);
      setExcludeList(nextCampaignData.excludeList ?? []);
      setReplyTo("");
    },
    [closeDraft, initialStepIndex, resetSelection, setExcludeList, setReplyTo, setSendTo]
  );

  return {
    ...basics,
    ...selection,
    stepIndex,
    stepKey,
    campaignName,
    subjectLine,
    canProceed,
    updateCampaignName,
    setSubjectLine,
    goNext,
    goBack,
    goToStep,
    furthestIndex: furthestIndexRef.current,
    reset,
    draft: {
      draftId: draft.draftId,
      isSaving: draft.isSaving,
      hasUnsavedChanges,
      save: saveDraft,
      close: closeDraft,
    },
  };
};
